import React, { useEffect, useState } from "react";
import AppShell from "../components/AppShell";
import PageHero from "../components/PageHero";
import { api, formatError } from "../lib/api";
import { toast } from "sonner";
import { Bell, MailCheck, MessageSquareText, Phone, Save, ArrowLeft, Info } from "lucide-react";
import { Link } from "react-router-dom";
import { useMenuVisibility } from "../context/MenuVisibilityContext";
import { cn } from "../lib/utils";

const CHANNEL_META = [
  { key: "in_app", label: "In-app",     icon: Bell,               desc: "Bell dropdown + dashboard cards" },
  { key: "email",  label: "Email",      icon: MailCheck,          desc: "Delivered via Resend" },
  { key: "sms",    label: "WhatsApp / SMS", icon: MessageSquareText, desc: "Delivered via Twilio when the channel is live" },
];

export default function AccountNotifications() {
  const [data, setData] = useState(null);
  const [prefs, setPrefs] = useState(null);
  const [phone, setPhone] = useState("");
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  // Admin can flip the WhatsApp / SMS channel on or off from
  // Menu Directory → Pages → Notification Preferences. When OFF,
  // the SMS column stays visible so founders know the channel is
  // planned, but toggles are non-interactive and a "Coming Soon"
  // chip is shown in the header — no more "mocked" copy anywhere
  // on the page.
  const { isPageElementHidden } = useMenuVisibility();
  const smsDisabled = isPageElementHidden("notifications-channel-sms");

  useEffect(() => {
    setLoading(true);
    api.get("/account/notification-prefs")
      .then((r) => {
        setData(r.data);
        setPrefs(r.data.prefs);
        setPhone(r.data.phone_number || "");
      })
      .catch((err) => toast.error(formatError(err)))
      .finally(() => setLoading(false));
  }, []);

  const toggle = (catKey, channelKey) => {
    setPrefs((p) => ({
      ...p,
      [catKey]: { ...(p[catKey] || {}), [channelKey]: !(p[catKey] || {})[channelKey] },
    }));
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...prefs, phone_number: phone };
      const res = await api.put("/account/notification-prefs", payload);
      setPrefs(res.data.prefs);
      toast.success("Preferences saved");
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <AppShell title="Notification Preferences">
      <div className="w-full">
        <Link
          to="/account"
          data-testid="account-notifications-back"
          className="inline-flex items-center gap-1 text-xs font-semibold text-muted-foreground hover:text-primary transition mb-4"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> Back to account
        </Link>
        <PageHero
          eyebrow="Notification preferences"
          icon={Bell}
          title={<>How should Warren <span className="text-grad">reach you?</span></>}
          subtitle={smsDisabled
            ? "Choose exactly which categories notify you, and through which channel. In-app notifications land in the bell dropdown; email flows through Resend. WhatsApp / SMS is coming soon — admins can flip it on from Menu Directory → Pages once Twilio is live."
            : "Choose exactly which categories notify you, and through which channel. In-app notifications land in the bell dropdown; email flows through Resend; WhatsApp / SMS is delivered via Twilio."}
        />

        {loading && (
          <div className="surface p-8 text-center text-muted-foreground" data-testid="account-notifications-loading">
            Loading your preferences…
          </div>
        )}

        {!loading && prefs && data && (
          <>
            <div className="surface overflow-hidden" data-testid="account-notifications-matrix">
              <table className="w-full">
                <thead className="bg-shell-muted/50">
                  <tr>
                    <th className="text-left px-5 py-3 text-[10px] font-bold uppercase tracking-[0.14em] text-muted-foreground">
                      Category
                    </th>
                    {CHANNEL_META.map((ch) => {
                      const chDim = ch.key === "sms" && smsDisabled;
                      return (
                        <th
                          key={ch.key}
                          // Header stays at full opacity — only the toggles
                          // in the column body grey out. The "Coming Soon"
                          // chip is enough of a signal in the header itself.
                          className="px-3 py-3 text-center text-[10px] font-bold uppercase tracking-[0.14em] text-muted-foreground w-[128px]"
                          data-testid={ch.key === "sms" ? "prefs-sms-header" : undefined}
                          data-sms-state={ch.key === "sms" ? (smsDisabled ? "coming-soon" : "live") : undefined}
                        >
                          <div className="flex flex-col items-center gap-1">
                            <ch.icon className="w-4 h-4" />
                            <span>{ch.label}</span>
                            {ch.key === "sms" && smsDisabled && (
                              // Same treatment as the sidebar "Coming Soon"
                              // chip (see AppShell.jsx L988) so admins and
                              // founders recognise the label instantly.
                              <span
                                className="text-[9.5px] font-bold uppercase tracking-[0.14em] text-grad shrink-0"
                                data-testid="prefs-sms-coming-soon"
                              >
                                Coming Soon
                              </span>
                            )}
                          </div>
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {data.categories.map((cat) => (
                    <tr key={cat.key} className="border-t border-shell-border" data-testid={`prefs-row-${cat.key}`}>
                      <td className="px-5 py-4 align-top">
                        <p className="font-semibold text-sm">{cat.label}</p>
                        <p className="text-[12px] text-muted-foreground mt-0.5">{cat.desc}</p>
                      </td>
                      {CHANNEL_META.map((ch) => {
                        const isOn = !!(prefs[cat.key] || {})[ch.key];
                        const chDim = ch.key === "sms" && smsDisabled;
                        return (
                          <td
                            key={ch.key}
                            className={cn(
                              "px-3 py-4 text-center",
                              chDim && "opacity-50 pointer-events-none",
                            )}
                            aria-disabled={chDim || undefined}
                          >
                            <ToggleSwitch
                              checked={isOn && !chDim}
                              onChange={() => { if (!chDim) toggle(cat.key, ch.key); }}
                              testid={`prefs-${cat.key}-${ch.key}`}
                              ariaLabel={`${cat.label} via ${ch.label}${chDim ? " (coming soon)" : ""}`}
                            />
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div
              className={cn(
                "surface p-5 mt-6 transition-opacity",
                // Full-card grey-out when Menu Directory has the SMS
                // channel disabled — matches the table column treatment
                // so the two surfaces stay in lock-step.
                smsDisabled && "opacity-50 pointer-events-none",
              )}
              data-testid="account-notifications-phone"
              aria-disabled={smsDisabled || undefined}
              data-sms-state={smsDisabled ? "coming-soon" : "live"}
            >
              <div className="flex items-center gap-3 mb-2">
                <Phone className="w-4 h-4 text-primary" />
                <p className="font-semibold text-sm">WhatsApp / SMS phone number</p>
                {smsDisabled && (
                  <span
                    className="text-[9.5px] font-bold uppercase tracking-[0.14em] text-grad shrink-0"
                    data-testid="account-notifications-phone-coming-soon"
                  >
                    Coming Soon
                  </span>
                )}
              </div>
              <p className="text-[12px] text-muted-foreground mb-3">
                Include country code (e.g. <code>+15551234567</code>). We'll save it now so that once WhatsApp/SMS goes live, messaging flips on automatically for the categories you've enabled above.
              </p>
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="+1 555 123 4567"
                data-testid="account-notifications-phone-input"
                disabled={smsDisabled}
                className="w-full sm:w-96 rounded-lg border border-border bg-white px-3 py-2 text-sm focus:outline-none focus:border-primary disabled:bg-shell-muted/40 disabled:text-muted-foreground disabled:cursor-not-allowed"
              />
              <div className="mt-3 flex items-start gap-2 text-[12px] text-pink-800 bg-pink-50/70 border border-pink-200 rounded-lg p-3">
                <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                <span>
                  {smsDisabled
                    ? <>The WhatsApp / SMS channel is <b>coming soon</b>. Save your number here and messaging will flip on automatically once an admin enables the channel from Menu Directory → Pages.</>
                    : <>Save your number here and messaging will flip on for any category you enable above.</>}
                </span>
              </div>
            </div>

            <div className="mt-6 flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={save}
                disabled={saving}
                data-testid="account-notifications-save"
                className="btn-primary text-sm inline-flex items-center gap-1.5"
              >
                <Save className="w-3.5 h-3.5" />
                {saving ? "Saving…" : "Save preferences"}
              </button>
            </div>
          </>
        )}
      </div>
    </AppShell>
  );
}

function ToggleSwitch({ checked, onChange, testid, ariaLabel }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={ariaLabel}
      onClick={onChange}
      data-testid={testid}
      className={[
        "relative inline-flex h-5 w-9 shrink-0 rounded-full border transition-colors",
        checked ? "bg-primary border-primary" : "bg-shell-muted border-shell-border",
      ].join(" ")}
    >
      <span
        className={[
          "inline-block h-4 w-4 rounded-full bg-white shadow transform transition-transform mt-[1px]",
          checked ? "translate-x-4" : "translate-x-[1px]",
        ].join(" ")}
      />
    </button>
  );
}
